import { useIsDesktop, T, Bone } from '../reportTokens';
import KpiTooltip from '../../KpiTooltip';

/*
 * SalesWaterfall — the single shared "Sales breakdown waterfall" (ZAR-1462).
 *
 * ONE component consumed identically by the Snapshot, Sales and EOD-summary reports —
 * no per-report variants. Its shape is driven by the active sales-source toggle, because
 * data completeness differs by source (Brisque never renders a $0 deduction row for a
 * source where the value is simply unknown — unknown ≠ zero):
 *
 *   • direct   → complete two-zone ladder (Revenue → Net Sales → Cash reconciliation)
 *   • overall  → same ladder, blended (Net Sales carries an asterisk + footnote)
 *   • thirdparty → honest 3-line view (Item Total + Tax = Total Paid); no Net line, no
 *                  deduction rows, no green hero bar — plus a persistent info note.
 *
 * Rendering is driven off a per-source ROW MANIFEST, not off values, so zero-data still
 * renders each source's available rows at $0 and the ladder stays structurally complete.
 */

export type WaterfallSource = 'overall' | 'direct' | 'thirdparty';

export interface SalesWaterfallData {
    // Revenue zone (direct / overall)
    grossSales: number;      // menu value, pre-tax. SUMMED: Net + Voids + Discounts + Comps + Refunds
    voids: number;
    discounts: number;
    complimentary: number;
    refunds: number;
    netSales: number;        // green hero
    // Cash reconciliation zone (direct / overall)
    tax: number;             // all sources
    tipsAndGratuity: number; // Direct only
    otherCharges: number;    // "Other" stamp KPI (service charges, packaging, etc.)
    totalCollected: number;  // "Total Collected" stamp KPI (grandTotal) — taken, not derived
    // 3rd-party honest view
    itemTotal: number;       // 3rd-party menu revenue
    totalPaid: number;       // = Item Total + Tax
}

/*
 * Raw per-source KPI inputs → SalesWaterfallData (ZAR-1462 value mapping).
 * Each field comes from the same API/value that backs its standalone stamp KPI:
 *   voids          ← Snapshot "Void" (void-summary)
 *   discounts      ← Snapshot "Discounts" (snapshot-details discount)
 *   complimentary  ← Snapshot "Complimentary" (comp-order-log)
 *   refunds        ← Snapshot "Refund" (refund-summary)
 *   tax            ← Sales "Total Tax" (snapshot-details tax)
 *   tips+gratuity  ← Sales "Tips" + "Gratuity"
 *   otherCharges   ← Sales "Other" stamp KPI (snapshot-details otherCharges)
 *   totalCollected ← Sales "Total Collected" stamp KPI (snapshot-details grandTotal)
 *
 * Gross Sales is NOT taken from an API field — it is summed from the ladder it heads, so the
 * top row always reconciles with the rows beneath it:
 *   Gross = Voids + Discounts + Complimentary + Refunds + Net Sales
 * Total Collected is the opposite: it is the stamp KPI value verbatim (never re-derived), so
 * the waterfall's end row and the standalone stamp KPI can never disagree.
 * Derived: 3rd-party Total Paid = Item Total + Tax.
 */
export interface WaterfallInputs {
    voids: number;
    discounts: number;
    complimentary: number;
    refunds: number;
    netSales: number;
    tax: number;
    tips: number;
    gratuity: number;
    otherCharges: number;
    /** "Total Collected" stamp KPI value (grandTotal) — displayed as-is. */
    totalCollected: number;
}

export function buildWaterfallData(inputs: WaterfallInputs): SalesWaterfallData {
    const n = (x: number) => Number(x) || 0;
    const netSales = n(inputs.netSales);
    const voids = n(inputs.voids);
    const discounts = n(inputs.discounts);
    const complimentary = n(inputs.complimentary);
    const refunds = n(inputs.refunds);
    const tax = n(inputs.tax);
    const tips = n(inputs.tips);
    return {
        grossSales: voids + discounts + complimentary + refunds + netSales,
        voids,
        discounts,
        complimentary,
        refunds,
        netSales,
        tax,
        tipsAndGratuity: tips + n(inputs.gratuity),
        otherCharges: n(inputs.otherCharges),
        totalCollected: n(inputs.totalCollected), // stamp KPI value, not derived
        itemTotal: netSales,                      // 3rd-party menu revenue
        totalPaid: netSales + tax,                // 3rd-party: Item Total + Tax
    };
}

/** The "Total paid" mini-KPI value for a given source. */
export const getTotalPaidValue = (source: WaterfallSource, data: SalesWaterfallData): number =>
    source === 'thirdparty' ? data.totalPaid : data.totalCollected;

/*
 * The mini-KPI above the waterfall must read exactly like the waterfall's own end row,
 * so both are derived from the source here rather than hard-coded per screen:
 *   • direct / overall → "Total Collected" (the Sales screen's stamp KPI value)
 *   • thirdparty       → "Total Paid" (Item Total + Tax, on platform)
 */
export const getTotalPaidLabel = (source: WaterfallSource): string =>
    source === 'thirdparty' ? 'Total Paid' : 'Total Collected';

/** Subtitle rendered under the mini-KPI value; 3rd-party carries no tips, so it has none. */
export const getTotalPaidSub = (source: WaterfallSource): string | undefined =>
    source === 'thirdparty' ? undefined : 'Incl. Tax & Tips';

type DeductionKey = 'voids' | 'discounts' | 'complimentary' | 'refunds';

interface SalesWaterfallProps {
    source: WaterfallSource;
    data: SalesWaterfallData;
    fmt: (v: number) => string;
    loading?: boolean;
    /** Optional: make deduction labels navigable (matches the "Voids ›" links). */
    onDeductionClick?: (key: DeductionKey) => void;
    /** Card chrome is on by default; pass false when the host already provides a card. */
    card?: boolean;
}

type RowKind = 'start' | 'deduction' | 'net' | 'addition' | 'end';

interface Row {
    key: string;
    kind: RowKind;
    label: string;
    sub?: string;
    amount: number;
    info?: string;
    deductionKey?: DeductionKey;
    asterisk?: boolean;
}

const THIRD_PARTY_NOTE =
    "Third-party platforms report only item total, tax, and total paid. Commissions, platform discounts, and comps aren't shared with Brisque, so a true net for these orders can't be shown here.";

const OVERALL_FOOTNOTE =
    "Third-party orders are included at item total (pre-commission). Platform fees aren't reported to Brisque, so blended Net Sales is slightly overstated for third-party volume.";

const BAR_SLATE = '#5A6B84';

function buildRows(source: WaterfallSource, d: SalesWaterfallData): Row[] {
    if (source === 'thirdparty') {
        return [
            { key: 'itemTotal', kind: 'start', label: 'Item Total', sub: '3rd-party menu revenue', amount: d.itemTotal, info: 'Menu-item revenue reported by the third-party platform.' },
            { key: 'tax', kind: 'addition', label: 'Tax', sub: '(pass-through)', amount: d.tax, info: 'Platform-reported tax. Never computed as Gross × rate.' },
            { key: 'totalPaid', kind: 'end', label: 'Total Paid', sub: 'on platform', amount: d.totalPaid, info: 'Total the customer paid on the third-party platform.' },
        ];
    }
    // direct / overall — full two-zone ladder
    return [
        { key: 'gross', kind: 'start', label: 'Gross Sales', sub: 'menu value, pre-tax', amount: d.grossSales, info: 'Full menu value before any deductions or tax — the sum of voids, discounts, complimentary, refunds and net sales.' },
        { key: 'voids', kind: 'deduction', label: 'Voids', amount: d.voids, deductionKey: 'voids' },
        { key: 'discounts', kind: 'deduction', label: 'Discounts', amount: d.discounts, deductionKey: 'discounts' },
        { key: 'complimentary', kind: 'deduction', label: 'Complimentary', amount: d.complimentary, deductionKey: 'complimentary' },
        { key: 'refunds', kind: 'deduction', label: 'Refunds', amount: d.refunds, deductionKey: 'refunds' },
        { key: 'net', kind: 'net', label: 'Net Sales', amount: d.netSales, asterisk: source === 'overall', info: 'Revenue you keep: Gross less voids, discounts, comps and refunds.' },
        { key: 'tax', kind: 'addition', label: 'Tax collected', sub: '(pass-through)', amount: d.tax, info: 'Collected on behalf of the tax authority — not your revenue.' },
        { key: 'tips', kind: 'addition', label: 'Tips & Gratuity', sub: source === 'overall' ? '(pass-through · Direct only)' : '(pass-through)', amount: d.tipsAndGratuity, info: 'Paid straight to staff. Third-party tips are handled on-platform and not reported.' },
        { key: 'others', kind: 'addition', label: 'Others', sub: '(other charges)', amount: d.otherCharges, info: 'Other charges collected on the bill — service charges, packaging and similar fees.' },
        { key: 'total', kind: 'end', label: 'Total Collected', amount: d.totalCollected, info: 'Cash actually collected from customers, including tax, tips and other charges.' },
    ];
}

// Waterfall bar geometry: cumulative running totals expressed as fractions of `scale`.
function computeGeometry(rows: Row[], source: WaterfallSource) {
    const scale = source === 'thirdparty'
        ? Math.max(1, /* itemTotal */ (rows[0]?.amount ?? 0), /* totalPaid */ (rows[2]?.amount ?? 0))
        : Math.max(1, ...rows.filter(r => r.kind === 'start' || r.kind === 'end' || r.kind === 'net').map(r => r.amount));

    const geo: Record<string, { start: number; end: number }> = {};
    let running = 0;
    for (const r of rows) {
        if (r.kind === 'start') {
            geo[r.key] = { start: 0, end: r.amount / scale };
            running = r.amount;
        } else if (r.kind === 'deduction') {
            const before = running;
            const after = running - r.amount;
            geo[r.key] = { start: after / scale, end: before / scale };
            running = after;
        } else if (r.kind === 'net') {
            geo[r.key] = { start: 0, end: r.amount / scale };
            running = r.amount; // cash-reconciliation zone builds up from Net
        } else if (r.kind === 'addition') {
            const before = running;
            const after = running + r.amount;
            geo[r.key] = { start: before / scale, end: after / scale };
            running = after;
        } else { // end
            geo[r.key] = { start: 0, end: r.amount / scale };
        }
    }
    return geo;
}

const barColorFor = (kind: RowKind) =>
    kind === 'deduction' ? T.accent : kind === 'net' ? T.green : BAR_SLATE;

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
    <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: T.t3,
        textTransform: 'uppercase', margin: '4px 0 6px',
        display: 'flex', alignItems: 'center', gap: 8,
    }}>
        <span>{children}</span>
        <span style={{ flex: 1, height: 1, background: T.divider }} />
    </div>
);

const SalesWaterfall = ({ source, data, fmt, loading, onDeductionClick, card = true }: SalesWaterfallProps) => {
    const isDesktop = useIsDesktop();
    const rows = buildRows(source, data);
    const geo = computeGeometry(rows, source);
    const isThirdParty = source === 'thirdparty';

    const title = isThirdParty ? '3rd-Party Sales' : 'Sales Waterfall';
    const subtitle = isThirdParty
        ? 'Only what the platform reports'
        : 'From the full menu value down to the revenue you keep, then reconciled up to the cash collected';

    const renderRow = (r: Row, idx: number) => {
        const g = geo[r.key] ?? { start: 0, end: 0 };
        const barColor = barColorFor(r.kind);
        const isDeduction = r.kind === 'deduction';
        const clickable = isDeduction && !!onDeductionClick;

        const signed = isDeduction ? `-${fmt(r.amount)}` : r.kind === 'addition' ? `+${fmt(r.amount)}` : fmt(r.amount);
        const valueColor = isDeduction ? T.accent : r.kind === 'net' ? T.green : r.kind === 'addition' ? T.t2 : T.t1;
        const labelBold = r.kind === 'start' || r.kind === 'net' || r.kind === 'end';

        const label = (
            <span style={{ display: 'inline-flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
                <span
                    onClick={clickable ? () => onDeductionClick!(r.deductionKey!) : undefined}
                    style={{
                        fontSize: 13,
                        fontWeight: labelBold ? 700 : 500,
                        color: clickable ? T.accent : r.kind === 'net' ? T.green : T.t1,
                        textDecoration: clickable ? 'underline' : 'none',
                        cursor: clickable ? 'pointer' : 'default',
                    }}
                >
                    {r.label}{r.asterisk ? ' *' : ''}{clickable ? ' ›' : ''}
                </span>
                {r.sub && <span style={{ fontSize: 11, color: T.t3 }}>{r.sub}</span>}
                {r.info && <KpiTooltip text={r.info} isMobile={!isDesktop} />}
            </span>
        );

        const bar = loading ? (
            <Bone w="100%" h={20} r={5} />
        ) : (
            <div style={{ position: 'relative', height: 20, background: T.hover, borderRadius: 5, overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: `${Math.max(0, g.start) * 100}%`,
                    width: `${Math.max(0, g.end - g.start) * 100}%`,
                    minWidth: 3, background: barColor, borderRadius: 5,
                }} />
            </div>
        );

        const value = (
            <div style={{ fontSize: 13, fontWeight: 700, color: valueColor, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {loading ? <Bone w={54} h={14} r={4} style={{ display: 'inline-block' }} /> : signed}
            </div>
        );

        // SW5 — the Net Sales row previously got a full-row greenSoft background, unlike every
        // other row (all transparent); the green text/bar it already carries (barColorFor,
        // valueColor) is enough to mark it as the hero row without a one-off row treatment.
        if (isDesktop) {
            return (
                <div key={r.key} style={{
                    display: 'grid', gridTemplateColumns: '260px 1fr 96px', alignItems: 'center',
                    columnGap: 16, padding: '7px 8px', borderRadius: 8,
                    marginTop: idx === 0 ? 0 : 2,
                }}>
                    <div>{label}</div>
                    {bar}
                    {value}
                </div>
            );
        }
        return (
            <div key={r.key} style={{ padding: '7px 8px', borderRadius: 8, marginTop: idx === 0 ? 0 : 4 }}>
                <div style={{ marginBottom: 5 }}>{label}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 84px', alignItems: 'center', columnGap: 12 }}>
                    {bar}
                    {value}
                </div>
            </div>
        );
    };

    // Split the ladder into its two zones (direct / overall only).
    const netIdx = rows.findIndex(r => r.kind === 'net');
    const revenueRows = isThirdParty ? [] : rows.slice(0, netIdx + 1);
    const cashRows = isThirdParty ? [] : rows.slice(netIdx + 1);

    const body = (
        <>
            {/* SW5 — matches the sibling section headings this widget sits next to (e.g.
                Snapshot's "Hourly Revenue", .snapshot-drill-title: 13px/600) instead of its own
                larger 16px/700, which read as oversized next to them. */}
            <div style={{ fontSize: 13, fontWeight: 600, color: T.t1 }}>{title}</div>
            <div style={{ fontSize: 11, color: T.t3, marginTop: 3, marginBottom: 14 }}>{subtitle}</div>

            {isThirdParty ? (
                <>
                    <div>{rows.map((r, i) => renderRow(r, i))}</div>
                    <div style={{
                        display: 'flex', gap: 10, marginTop: 14, padding: '12px 14px',
                        background: T.blueSoft, borderRadius: 10, alignItems: 'flex-start',
                    }}>
                        {/* SW5 — was italic to fake a slanted "i" glyph; every other info icon in
                            this component (KpiTooltip) is upright, so this one stood out. */}
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 18, height: 18, borderRadius: '50%', background: T.blue, color: '#fff',
                            fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1,
                        }}>i</span>
                        <div style={{ fontSize: 12.5, color: T.t2, lineHeight: 1.5 }}>{THIRD_PARTY_NOTE}</div>
                    </div>
                </>
            ) : (
                <>
                    <SectionHeader>Revenue (your money)</SectionHeader>
                    <div>{revenueRows.map((r, i) => renderRow(r, i))}</div>
                    <div style={{ marginTop: 12 }}>
                        <SectionHeader>Cash reconciliation (not your revenue)</SectionHeader>
                    </div>
                    <div>{cashRows.map((r, i) => renderRow(r, i))}</div>
                    {source === 'overall' && (
                        <div style={{ fontSize: 11.5, color: T.t3, marginTop: 14, lineHeight: 1.5 }}>
                            * {OVERALL_FOOTNOTE}
                        </div>
                    )}
                </>
            )}
        </>
    );

    if (!card) return body;
    return (
        <div style={{
            background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14,
            padding: isDesktop ? '20px 22px' : '16px 14px', boxShadow: T.shadow,
        }}>
            {body}
        </div>
    );
};

export default SalesWaterfall;
